import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useCallback, useMemo } from 'react';
// utils
import { getAllPartnersOfUser } from '../services/register/getAllPartnersOfUser';
import axios from '../utils/axios';
//
import { setSession } from './utils';
import { getUserData } from '../services/register/getUserData';

// ----------------------------------------------------------------------

const initialState = {
  isInitialized: false,
  isAuthenticated: false,
  user: null,
};

const reducer = (state, action) => {
  if (action.type === 'INITIAL') {
    return {
      isInitialized: true,
      isAuthenticated: action.payload.isAuthenticated,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGIN') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'REGISTER') {
    return {
      ...state,
      isAuthenticated: true,
      user: action.payload.user,
    };
  }
  if (action.type === 'LOGOUT') {
    return {
      ...state,
      isAuthenticated: false,
      user: null,
    };
  }

  return state;
};

// ----------------------------------------------------------------------

export const AuthContext = createContext(null);

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const initialize = useCallback(async () => {
    try {
      const _id = localStorage.getItem('_id');
      if (_id) {
        const loggedInUserId = localStorage.getItem('loginUserId') || _id;
        if (!localStorage.getItem('loginUserId')) {
          localStorage.setItem('loginUserId', loggedInUserId);
        }
        const userData = await getUserData(loggedInUserId);
        setSession(JSON.stringify(userData), loggedInUserId);

        let partnerId = '';
        const isManagingChildPartner = Boolean(localStorage.getItem('partnerIdmanage'));
        if (isManagingChildPartner) {
          partnerId = localStorage.getItem('partnerIdmanage');
        } else {
          partnerId = localStorage.getItem('partnerId');
        }

        const allPartnersOfUser = await getAllPartnersOfUser();

        let partner = allPartnersOfUser.find(
          (item) => (item.partner_type === 'affiliate' ? item.parent : item._id) === partnerId
        );

        if (!partner && isManagingChildPartner) {
          const fallbackPartnerId = localStorage.getItem('partnerId');
          partner = allPartnersOfUser.find(
            (item) =>
              (item.partner_type === 'affiliate' ? item.parent : item._id) === fallbackPartnerId
          );
        }

        if (partner) {
          if (!isManagingChildPartner || !localStorage.getItem('currentPartner')) {
            localStorage.setItem('currentPartner', JSON.stringify(partner));
          }
          dispatch({
            type: 'INITIAL',
            payload: {
              isAuthenticated: true,
              user: userData,
            },
          });
        } else {
          dispatch({
            type: 'INITIAL',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } else {
        dispatch({
          type: 'INITIAL',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    } catch (error) {
      console.error(error);
      dispatch({
        type: 'INITIAL',
        payload: {
          isAuthenticated: false,
          user: null,
        },
      });
    }
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Everything that happens once credentials are accepted. Split out of login()
  // because an account with two-step sign-in finishes at a different endpoint
  // (/auth/2fa/verify) but has to land in exactly the same state.
  const completeLogin = useCallback(async (loginResponse) => {
    const { _id } = loginResponse;

    // Clear stale session so header and x-user-id match the new login
    localStorage.removeItem('currentUser');
    localStorage.removeItem('parent');
    localStorage.removeItem('partnerIdmanage');

    const userData = await getUserData(_id);
    setSession(JSON.stringify(userData), _id);
    localStorage.setItem('loginUserId', _id);

    let partnerId = '';
    if (localStorage.getItem('partnerIdmanage')) {
      partnerId = localStorage.getItem('partnerIdmanage');
    } else {
      partnerId = localStorage.getItem('partnerId');
    }
    const allPartnersOfUser = await getAllPartnersOfUser();

    const partner = allPartnersOfUser.find(
      (item) => (item.partner_type === 'affiliate' ? item.parent : item._id) === partnerId
    );

    if (partner) {
      localStorage.setItem('currentPartner', JSON.stringify(partner));
      dispatch({
        type: 'LOGIN',
        payload: {
          user: userData,
        },
      });
    } else {
      throw new Error('You are not authorized to access this partner');
    }
  }, []);

  // LOGIN
  const login = useCallback(
    async (email, password) => {
      try {
        const data = new URLSearchParams();
        data.append('email', email);
        data.append('password', password);
        const response = await axios({
          method: 'post',
          url: '/auth/login',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            // Tells the gateway this app can render a two-step challenge.
            // Without it an enrolled account is refused outright rather than
            // handed a challenge it cannot display.
            'x-2fa-capable': '1',
          },
          data,
        });
        const loginResponse = await response.data;

        // A second step is owed. Nothing may be written to storage here — the
        // guards treat a stored session as proof of being signed in, so a
        // reload mid-challenge would otherwise land inside the app.
        if (loginResponse?.twoFactorRequired) {
          return loginResponse;
        }

        await completeLogin(loginResponse);
        return loginResponse;
      } catch (error) {
        throw new Error(error.message);
      }
    },
    [completeLogin]
  );

  const verifyTwoFactor = useCallback(
    async (challengeToken, code, trustDevice) => {
      try {
        const response = await axios({
          method: 'post',
          url: '/auth/2fa/verify',
          data: { challengeToken, code, trustDevice },
        });
        await completeLogin(response.data);
        return response.data;
      } catch (error) {
        throw new Error(error.message || 'Unable to verify that code');
      }
    },
    [completeLogin]
  );

  const resendTwoFactorCode = useCallback(async (challengeToken) => {
    try {
      const partnerId = localStorage.getItem('partnerId');
      const response = await axios({
        method: 'post',
        url: '/auth/2fa/resend',
        data: { challengeToken, partnerId },
      });
      return response.data;
    } catch (error) {
      throw new Error(error.message || 'Unable to send a new code');
    }
  }, []);

  // MANAGE ACCOUNT
  const manageAccount = async (access_token, refresh_token) => {
    try {
      const userProfile = await axios.get('/lms_user/api/user-profile', {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
      const res = userProfile.data;
      const profile = JSON.stringify(res);
      setSession(access_token, profile, refresh_token);

      dispatch({
        type: 'LOGIN',
      });
    } catch (error) {
      console.log(error);
    }
  };

  // REGISTER
  const register = useCallback(async (email, password, firstName, lastName) => {
    const response = await axios.post('/auth/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  }, []);

  // LOGOUT
  const logout = useCallback(async () => {
    await axios({
      method: 'get',
      url: '/auth/logout',
    });
    setSession(null, null);
    localStorage.removeItem('currentPartner');
    localStorage.removeItem('partnerIdmanage');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('loginUserId');
    dispatch({ type: 'LOGOUT' });
  }, []);

  const memoizedValue = useMemo(
    () => ({
      isInitialized: state.isInitialized,
      isAuthenticated: state.isAuthenticated,
      user: state.user,
      method: 'jwt',
      login,
      verifyTwoFactor,
      resendTwoFactorCode,
      register,
      logout,
      manageAccount,
    }),
    [
      state.isAuthenticated,
      state.isInitialized,
      state.user,
      login,
      verifyTwoFactor,
      resendTwoFactorCode,
      logout,
      register,
    ]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
